import salesOrdersServiceGen from "./generated/salesOrdersServiceGen";
import { fetchWithAuth } from "../../utils/httpInterceptor";

class salesOrdersService extends salesOrdersServiceGen {
  static async downloadSourcePdf(dataAreaId, iSalesId) {
    return this.downloadSourceFile(dataAreaId, iSalesId, "source-pdf");
  }

  static async downloadSourceEmail(dataAreaId, iSalesId) {
    return this.downloadSourceFile(dataAreaId, iSalesId, "source-email");
  }

  static async downloadSourceJson(dataAreaId, iSalesId) {
    return this.downloadSourceFile(dataAreaId, iSalesId, "source-json");
  }

  static async downloadDirectionReport(dataAreaId, iSalesId) {
    return this.downloadSourceFile(dataAreaId, iSalesId, "direction-report");
  }

  static async isDirectionReportAvailable(dataAreaId, iSalesId) {
    const availability = await this.getSourceFilesAvailability(dataAreaId, iSalesId);
    return Boolean(availability.direction);
  }

  static async downloadConfirmReport(dataAreaId, iSalesId) {
    return this.downloadSourceFile(dataAreaId, iSalesId, "confirm-report");
  }

  static async isConfirmReportAvailable(dataAreaId, iSalesId) {
    const availability = await this.getSourceFilesAvailability(dataAreaId, iSalesId);
    return Boolean(availability.confirm);
  }

  // Triggers AX4 (via reverse sync business action SALES_ORDER_GENERATE_DIRECTION_REPORT)
  // to (re)generate the "Listado de direccion" PDF for this order and drop it into the
  // order's source folder. Useful for orders that never went through whatever automatic
  // flow normally produces this file (e.g. orders not auto-created from email/PDF, or
  // orders that had a placeholder item resolved manually). Fire-and-forget: the file
  // shows up asynchronously once AX4/the reverse-sync worker processes it - callers
  // should just poll getSourceFilesAvailability afterwards.
  static async requestDirectionReport(dataAreaId, iSalesId) {
    const safeDataAreaId = encodeURIComponent(String(dataAreaId || ""));
    const safeISalesId = encodeURIComponent(String(iSalesId || ""));
    const endpoint = `/api/legacy/sales-table/${safeDataAreaId}/${safeISalesId}/direction-report`;
    const response = await fetchWithAuth(endpoint, { method: "POST" });
    if (!response.ok) {
      let message = "";
      try { message = await response.text(); } catch { /* ignore */ }
      throw new Error(message || `Error ${response.status} solicitando el listado de direccion`);
    }
    return true;
  }

  // Triggers AX4 (via reverse sync business action SALES_ORDER_CONFIRM) to confirm
  // this order and generate its confirmation PDF into the order's source folder.
  // Unlike requestDirectionReport, this is not a read-only report: AX actually
  // registers the confirmation (ConfirmId, CustConfirmJour/CustConfirmTrans,
  // inventory reservation state). Fire-and-forget, same as requestDirectionReport.
  static async requestConfirmReport(dataAreaId, iSalesId) {
    const safeDataAreaId = encodeURIComponent(String(dataAreaId || ""));
    const safeISalesId = encodeURIComponent(String(iSalesId || ""));
    const endpoint = `/api/legacy/sales-table/${safeDataAreaId}/${safeISalesId}/confirm-report`;
    const response = await fetchWithAuth(endpoint, { method: "POST" });
    if (!response.ok) {
      let message = "";
      try { message = await response.text(); } catch { /* ignore */ }
      throw new Error(message || `Error ${response.status} solicitando la confirmación de pedido`);
    }
    return true;
  }

  static async getSourceFilesAvailability(dataAreaId, iSalesId) {
    const safeDataAreaId = encodeURIComponent(String(dataAreaId || ""));
    const safeISalesId = encodeURIComponent(String(iSalesId || ""));
    const endpoint = `/api/legacy/sales-table/${safeDataAreaId}/${safeISalesId}/source-files/availability`;
    const response = await fetchWithAuth(endpoint, { method: "GET" });
    return response.json();
  }

  static async downloadSourceFile(dataAreaId, iSalesId, kind) {
    const safeDataAreaId = encodeURIComponent(String(dataAreaId || ""));
    const safeISalesId = encodeURIComponent(String(iSalesId || ""));
    const endpoint = `/api/legacy/sales-table/${safeDataAreaId}/${safeISalesId}/${kind}`;
    const response = await fetchWithAuth(endpoint, { method: "GET" });
    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition") || "";
    const filename = this.extractFileNameFromContentDisposition(contentDisposition) || `${safeISalesId}-${kind}`;
    return { blob, filename };
  }

  static extractFileNameFromContentDisposition(contentDisposition) {
    if (!contentDisposition) {
      return null;
    }
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }
    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return plainMatch?.[1] || null;
  }
}

export default salesOrdersService;
